/* eslint-disable no-console */
/**
 * For command history tracking and undo functionality.
 * @module history
 * @license MIT
 * @copyright 2010 Jeff Schiller
 */

import { isNullish } from './utilities';
import SvgCanvas from './svgcanvas';
import { Command, ChangeElementCommand, BatchCommand } from './undocommands';
import { zenDataServiceInstance } from '@/services';

/**
* Group: Undo/Redo history management.
*/
export const HistoryEventTypes = {
    BEFORE_APPLY: 'before_apply',
    AFTER_APPLY: 'after_apply',
    BEFORE_UNAPPLY: 'before_unapply',
    AFTER_UNAPPLY: 'after_unapply'
};


export interface HistoryEventHandler {
    getCanvas(): SvgCanvas;
    handleHistoryEvent: (eventType: string, command: Command) => void;
}



// Todo: Figure out why the interface members aren't showing
//   up (with or without modules applied), despite our apparently following
//   http://usejsdoc.org/tags-interface.html#virtual-comments

/**
 * An interface that all command objects must implement.
 * @interface module:history.HistoryCommand
*/
/**
 * Applies.
 *
 * @function module:history.HistoryCommand#apply
 * @param {module:history.HistoryEventHandler} handler
 * @fires module:history~Command#event:history
 * @returns {void|true}
 */
/**
 *
 * Unapplies.
 * @function module:history.HistoryCommand#unapply
 * @param {module:history.HistoryEventHandler} handler
 * @fires module:history~Command#event:history
 * @returns {void|true}
 */
/**
 * Returns the elements.
 * @function module:history.HistoryCommand#elements
 * @returns {Element[]}
 */
/**
 * Gets the text.
 * @function module:history.HistoryCommand#getText
 * @returns {string}
 */
/**
 * Gives the type.
 * @function module:history.HistoryCommand.type
 * @returns {string}
 */

/**
 * @event module:history~Command#event:history
 * @type {module:history.HistoryCommand}
 */

/**
 * An interface for objects that will handle history events.
 * @interface module:history.HistoryEventHandler
 */
/**
 *
 * @function module:history.HistoryEventHandler#handleHistoryEvent
 * @param {string} eventType One of the HistoryEvent types
 * @param {module:history~Command#event:history} command
 * @listens module:history~Command#event:history
 * @returns {void}
 *
 */

export interface IUndoableChange {
    attrName: string;
    oldValues: any;
    elements: SVGGraphicsElement[]
}

/**
*
*/
export class UndoManager {
    private handler_: HistoryEventHandler;
    private undoStackPointer: number = 0;
    private undoStack: Command[] = [];

    // this is the stack that stores the original values, the elements and
    // the attribute name for begin/finish
    private undoChangeStackPointer: number = -1;
    private undoableChangeStack: IUndoableChange[] = [];


    /**
    * @param {module:history.HistoryEventHandler} historyEventHandler
    */
    constructor(historyEventHandler: HistoryEventHandler) {
        this.handler_ = historyEventHandler || null;
    }

    /**
    * Resets the undo stack, effectively clearing the undo/redo history.
    * @returns {void}
    */
    resetUndoStack() {
        this.undoStack = [];
        this.undoStackPointer = 0;
    }

    /**
    * @returns {Integer} Current size of the undo history stack
    */
    getUndoStackSize() {
        return this.undoStackPointer;
    }

    /**
    * @returns {Integer} Current size of the redo history stack
    */
    getRedoStackSize() {
        return this.undoStack.length - this.undoStackPointer;
    }

    /**
    * @returns {string} String associated with the next undo command
    */
    getNextUndoCommandText() {
        return this.undoStackPointer > 0 ? this.undoStack[this.undoStackPointer - 1].getText() : '';
    }

    /**
    * @returns {string} String associated with the next redo command
    */
    getNextRedoCommandText() {
        return this.undoStackPointer < this.undoStack.length ? this.undoStack[this.undoStackPointer].getText() : '';
    }

    /**
    * Performs an undo step.
    * @returns {void}
    */
    undo() {
        const cmd = this.getNextUndo(true);
        if (cmd) {
            console.log("Undoing ", cmd);
            cmd.unapply(this.handler_);

            const ev = new CustomEvent("history-changed", { detail: "undo" });
            window.dispatchEvent(ev);
            this.handler_.getCanvas().editor.refreshUI();
        }
    }

    /**
    * Performs a redo step.
    * @returns {void}
    */
    redo() {
        const cmd = this.getNextRedo(true);
        if (cmd) {
            console.log("Redoing ", cmd);
            cmd.apply(this.handler_);

            const ev = new CustomEvent("history-changed", { detail: "redo" });
            window.dispatchEvent(ev);
            this.handler_.getCanvas().editor.refreshUI();
        }
    }

    canUndo() {
        return this.undoStackPointer > 0;
    }

    canRedo() {
        return this.undoStackPointer < this.undoStack.length && this.undoStack.length > 0;
    }

    getNextUndo(alterStackPointer?: boolean) {
        let cmd = null;
        if (this.canUndo()) {
            cmd = this.undoStack[this.undoStackPointer - 1];

            if (alterStackPointer) {
                --this.undoStackPointer;
            }
        }

        return cmd;
    }

    getNextRedo(alterStackPointer?: boolean) {
        let cmd = null;
        if (this.canRedo()) {
            cmd = this.undoStack[this.undoStackPointer];

            if (alterStackPointer) {
                ++this.undoStackPointer;
            }
        }

        return cmd;
    }

    /**
    * Adds a command object to the undo history stack.
    * @param {Command} cmd - The command object to add
    * @returns {void}
    */
    addCommandToHistory(cmd: Command) {
        // TODO: we MUST compress consecutive text changes to the same element
        // (right now each keystroke is saved as a separate command that includes the
        // entire text contents of the text element)
        // TODO: consider limiting the history that we store here (need to do some slicing)

        // if our stack pointer is not at the end, then we have to remove
        // all commands after the pointer and insert the new command
        if (this.undoStackPointer < this.undoStack.length && this.undoStack.length > 0) {
            this.undoStack = this.undoStack.splice(0, this.undoStackPointer);
        }

        // If a BatchCommand can be unpacked, it has an internal command stack of 1 - it only holds a single inner command.
        // This essentially makes the batch useless, so we grab the inner command and make it the one we add to the undo stack.
        // Since unpackable batch commands can be nested multiple times, we repeat this process until we either get a different command
        // or one that cannot be unpacked. unpackCount is just an extra safety against infinite loops.
        let unpackCount = 0;
        while (cmd instanceof BatchCommand && cmd.canUnpack() && ++unpackCount < 256) {
            const oldCmd = cmd;
            cmd = cmd.stack[0];
            cmd.text = oldCmd.text;
            cmd.timestamp = oldCmd.timestamp;
        }

        // Can we merge this command with the previous one in the stack? If so, mergeHistory() returns true, so we set the currently
        // adding command to null, preventing its addition to the undo stack.
        if (this.undoStack.length > 0 && this.undoStack[this.undoStack.length - 1].mergeHistory(cmd, "Multiple changes")) {
            cmd = null;
        }

        if (cmd && !cmd.isNoOp()) {
            this.undoStack.push(cmd);
        }

        this.undoStackPointer = this.undoStack.length;
        
        const ev = new CustomEvent("history-changed", { detail: "new-entry" });
        window.dispatchEvent(ev);
    }

    /**
    * This function tells the canvas to remember the old values of the
    * `attrName` attribute for each element sent in.  The elements and values
    * are stored on a stack, so the next call to `finishUndoableChange()` will
    * pop the elements and old values off the stack, gets the current values
    * from the DOM and uses all of these to construct the undo-able command.
    * @param {string} attrName - The name of the attribute being changed
    * @param {Element[]} elems - Array of DOM elements being changed
    * @returns {void}
    */
    beginUndoableChange(attrName, elems) {
        // TODO: TEMP
        if(zenDataServiceInstance.GetDesignerMode() === "artwork") {
            return;
        }

        const p = ++this.undoChangeStackPointer;
        let i = elems.length;
        const oldValues = new Array(i), elements = new Array(i);
        while (i--) {
            const elem = elems[i];
            if (isNullish(elem)) { continue; }
            elements[i] = elem;
            oldValues[i] = elem.getAttribute(attrName);
        }
        this.undoableChangeStack[p] = {
            attrName,
            oldValues,
            elements
        };
    }

    /**
    * This function returns a `BatchCommand` object which summarizes the
    * change since `beginUndoableChange` was called.  The command can then
    * be added to the command history.
    * @returns {BatchCommand} Batch command object with resulting changes
    */
    finishUndoableChange() {
        // TODO: TEMP
        if(zenDataServiceInstance.GetDesignerMode() === "artwork") {
            return new BatchCommand('[DISABLED] Undo mechanism is disabled.');
        }

        const p = this.undoChangeStackPointer--;
        const changeset = this.undoableChangeStack[p];
        const { attrName } = changeset;
        const batchCmd = new BatchCommand('Change ' + attrName);
        let i = changeset.elements.length;

        while (i--) {
            const elem = changeset.elements[i];
            if (isNullish(elem)) {
                continue;
            }
            const changes = {};
            changes[attrName] = changeset.oldValues[i];

            if (changes[attrName] !== elem.getAttribute(attrName)) {
                batchCmd.addSubCommand(new ChangeElementCommand(elem, changes, attrName));
            }
        }

        this.undoableChangeStack[p] = null;
        return batchCmd;
    }
}
